import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { useAuth } from '../context/AuthContext';

interface PlanData {
    name: string;
    monthlyPrice: string;
    annualPrice?: string;
    monthlyPriceId?: string;
    annualPriceId?: string;
    period: string;
    limits: string[];
    features: string[];
    buttonText: string;
    buttonLink?: string;
    buttonStyle: string;
    featured?: boolean;
    isPaid?: boolean;
}

const PLANS: PlanData[] = [
    {
        name: 'Anonymous',
        monthlyPrice: '$0',
        period: 'forever',
        limits: [
            '1 conversion per day',
            'Max 5 target languages',
            'Max 10,000 rows per file',
            '10MB file size limit'
        ],
        features: [
            'No account required',
            'Basic XLIFF Export/Import'
        ],
        buttonText: 'Start Converting',
        buttonLink: '/',
        buttonStyle: 'secondary',
        featured: false,
        isPaid: false
    },
    {
        name: 'Free',
        monthlyPrice: '$0',
        period: 'forever',
        limits: [
            '2 conversions per day',
            'Max 10 target languages',
            'Max 10,000 rows per file',
            '20MB file size limit'
        ],
        features: [
            'Account Dashboard',
            'Higher Limits'
        ],
        buttonText: 'Sign Up Free',
        buttonLink: '/',
        buttonStyle: 'secondary',
        featured: false,
        isPaid: false
    },
    {
        name: 'Pro',
        monthlyPrice: '$10',
        annualPrice: '$99',
        monthlyPriceId: import.meta.env.VITE_PADDLE_STARTER_MONTHLY,
        annualPriceId: import.meta.env.VITE_PADDLE_STARTER_YEARLY,
        period: 'per month',
        limits: [
            'Unlimited conversions',
            'Unlimited target languages',
            'Unlimited rows',
            '30MB file size limit'
        ],
        features: [
            'Custom SRX Segmentation',
            'Priority Processing'
        ],
        buttonText: 'Subscribe',
        buttonStyle: 'primary',
        featured: true,
        isPaid: true
    }
];

// Plan hierarchy for upgrade/downgrade logic
const PLAN_HIERARCHY: Record<string, number> = {
    'Anonymous': 0,
    'Free': 1,
    'Pro': 2
};

export const PricingPage: React.FC = () => {
    const { user, openAuthModal } = useAuth();
    const [isAnnual, setIsAnnual] = useState(false);
    const [paddle, setPaddle] = useState<Paddle | undefined>(undefined);
    const [userPlan, setUserPlan] = useState<string | null>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
    const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);

    useEffect(() => {
        const initPaddle = async () => {
            try {
                const paddleInstance = await initializePaddle({
                    environment: 'production',
                    token: import.meta.env.VITE_PADDLE_CLIENT_SIDE_TOKEN,
                });
                setPaddle(paddleInstance);
            } catch (err) {
                console.error("Failed to initialize Paddle", err);
            }
        };
        initPaddle();
    }, []);

    // Fetch user's current plan
    useEffect(() => {
        const fetchUserPlan = async () => {
            if (!user?.token) {
                setUserPlan(null);
                setSubscriptionStatus(null);
                setNextBillingDate(null);
                return;
            }
            try {
                const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/user`, {
                    headers: { Authorization: `Bearer ${user.token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    // Capitalize first letter to match plan names
                    const plan = data.currentPlan ? data.currentPlan.charAt(0).toUpperCase() + data.currentPlan.slice(1) : 'Free';
                    setUserPlan(plan);
                    setSubscriptionStatus(data.subscriptionStatus || null);
                    setNextBillingDate(data.nextBillingDate || null);
                }
            } catch (err) {
                console.error('Failed to fetch user plan', err);
            }
        };
        fetchUserPlan();
    }, [user]);

    const handleSubscribe = (plan: PlanData) => {
        if (!paddle) {
            console.error('Paddle not initialized');
            return;
        }

        if (!user) {
            // Open auth modal for login/signup
            openAuthModal();
            return;
        }

        // Get the appropriate price ID based on billing cycle
        const priceId = isAnnual ? plan.annualPriceId : plan.monthlyPriceId;

        if (!priceId) {
            console.error('Price ID not found for plan:', plan.name);
            return;
        }

        paddle.Checkout.open({
            items: [{ priceId, quantity: 1 }],
            customData: { userId: user.userId },
            settings: {
                displayMode: 'overlay',
                theme: 'light',
                locale: 'en'
            }
        });
    };

    const getPrice = (plan: PlanData) => {
        if (!plan.isPaid) return plan.monthlyPrice;
        return isAnnual && plan.annualPrice ? plan.annualPrice : plan.monthlyPrice;
    };

    const getPeriod = (plan: PlanData) => {
        if (!plan.isPaid) return plan.period;
        return isAnnual ? 'per year' : 'per month';
    };

    const getSavings = (plan: PlanData) => {
        if (!plan.isPaid || !isAnnual || !plan.annualPrice) return null;
        const monthlyTotal = parseInt(plan.monthlyPrice.replace('$', '')) * 12;
        const annualPrice = parseInt(plan.annualPrice.replace('$', ''));
        const savings = monthlyTotal - annualPrice;
        return savings > 0 ? `Save $${savings}/year` : null;
    };

    const getButtonConfig = (plan: PlanData): { text: string; disabled: boolean; style: string; hidden: boolean } => {
        if (!user || !userPlan) {
            // Not logged in
            if (plan.isPaid) {
                return { text: 'Subscribe', disabled: false, style: 'primary', hidden: false };
            }
            return { text: plan.buttonText, disabled: false, style: plan.buttonStyle, hidden: false };
        }

        const currentLevel = PLAN_HIERARCHY[userPlan] || 1;
        const planLevel = PLAN_HIERARCHY[plan.name] || 0;

        // Current plan - show with green/primary style
        if (plan.name === userPlan) {
            return { text: 'Your Plan', disabled: true, style: 'primary', hidden: false };
        }

        // For subscribers (paid plans), hide buttons for Anonymous and Free
        if (currentLevel >= 2 && !plan.isPaid) {
            return { text: '', disabled: true, style: 'secondary', hidden: true };
        }

        // For non-subscribers viewing free plans
        if (!plan.isPaid) {
            return { text: plan.buttonText, disabled: false, style: plan.buttonStyle, hidden: false };
        }

        // Paid plans - show Upgrade or Downgrade
        if (planLevel > currentLevel) {
            return { text: 'Upgrade', disabled: false, style: 'primary', hidden: false };
        } else {
            return { text: 'Downgrade', disabled: false, style: 'secondary', hidden: false };
        }
    };

    const [managementUrls, setManagementUrls] = useState<{ updatePaymentMethodUrl?: string; cancelUrl?: string } | null>(null);
    const [loadingManagement, setLoadingManagement] = useState(false);
    const [showManageDropdown, setShowManageDropdown] = useState(false);

    const fetchManagementUrls = async () => {
        if (!user?.token || managementUrls) return;

        setLoadingManagement(true);
        try {
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/user/subscription-portal`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });

            if (res.ok) {
                const data = await res.json();
                setManagementUrls({
                    updatePaymentMethodUrl: data.updatePaymentMethodUrl,
                    cancelUrl: data.cancelUrl
                });
            }
        } catch (err) {
            console.error('Error fetching management URLs:', err);
        } finally {
            setLoadingManagement(false);
        }
    };

    // Fetch management URLs when user has active subscription
    useEffect(() => {
        if (user && subscriptionStatus === 'active' && userPlan && PLAN_HIERARCHY[userPlan] >= 2) {
            fetchManagementUrls();
        }
    }, [user, subscriptionStatus, userPlan]);

    return (
        <div className="page-container" style={{ maxWidth: '1400px' }}>
            <section className="hero">
                <h1 className="hero-title">Simple Pricing to Scale with You</h1>
                <p className="hero-subtitle">
                    Clear pricing with no hidden fees. Start for free and upgrade when you're ready.
                </p>
            </section>

            {/* Subscription Plans */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '1rem', padding: '0.5rem', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                    <span style={{ fontWeight: isAnnual ? 'normal' : 'bold', color: isAnnual ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                        Monthly
                    </span>
                    <button
                        onClick={() => setIsAnnual(!isAnnual)}
                        style={{
                            width: '50px',
                            height: '26px',
                            borderRadius: '13px',
                            border: 'none',
                            background: isAnnual ? 'var(--primary)' : 'var(--border)',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                        }}
                    >
                        <div style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '10px',
                            background: 'white',
                            position: 'absolute',
                            top: '3px',
                            left: isAnnual ? '27px' : '3px',
                            transition: 'left 0.2s'
                        }} />
                    </button>
                    <span style={{ fontWeight: isAnnual ? 'bold' : 'normal', color: isAnnual ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                        Annual <span style={{ color: 'var(--primary)', fontSize: '0.9em' }}>(Save ~17%)</span>
                    </span>
                </div>
            </div>

            <div className="pricing-grid" style={{ marginBottom: '4rem' }}>
                {PLANS.map((plan) => (
                    <div
                        key={plan.name}
                        className={`card pricing-card ${plan.featured ? 'featured' : ''}`}
                        style={plan.featured ? {
                            borderColor: 'var(--primary)',
                            borderWidth: '2px',
                            position: 'relative',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                        } : {}}
                    >
                        {plan.featured && (
                            <div style={{
                                position: 'absolute',
                                top: '-12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'var(--primary)',
                                color: 'white',
                                padding: '0.25rem 1rem',
                                borderRadius: 'var(--radius)',
                                fontSize: '0.6rem',
                                fontWeight: 'bold'
                            }}>
                                BEST VALUE
                            </div>
                        )}
                        <div className="pricing-header">
                            <h2>{plan.name}</h2>
                            <div className="price">{getPrice(plan)}</div>
                            <p className="period">{getPeriod(plan)}</p>
                            {getSavings(plan) && (
                                <div style={{ color: 'var(--primary)', fontSize: '0.9rem', fontWeight: 'bold', marginTop: '0.25rem' }}>
                                    {getSavings(plan)}
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Limits</div>
                            <ul className="pricing-features" style={{ marginBottom: 0 }}>
                                {plan.limits.map((limit, i) => (
                                    <li key={i} style={{ color: 'var(--text-primary)' }}>{limit}</li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Features</div>
                            <ul className="pricing-features">
                                {plan.features.map((feature, i) => (
                                    <li key={i}>{feature}</li>
                                ))}
                            </ul>
                        </div>

                        {(() => {
                            const btnConfig = getButtonConfig(plan);
                            if (btnConfig.hidden) {
                                return <div style={{ marginTop: 'auto', height: '40px' }} />;
                            }
                            if (plan.isPaid) {
                                return (
                                    <button
                                        onClick={() => handleSubscribe(plan)}
                                        className={`button ${btnConfig.style} full-width`}
                                        style={{ marginTop: 'auto', textAlign: 'center' }}
                                        disabled={btnConfig.disabled}
                                    >
                                        {btnConfig.text}
                                    </button>
                                );
                            }
                            return (
                                <Link
                                    to={plan.buttonLink!}
                                    className={`button ${btnConfig.style} full-width`}
                                    style={{ marginTop: 'auto', textAlign: 'center' }}
                                >
                                    {btnConfig.text}
                                </Link>
                            );
                        })()}
                    </div>
                ))}
            </div>

            {/* Subscription Management - only show for active subscriptions */}
            {user && subscriptionStatus === 'active' && userPlan && PLAN_HIERARCHY[userPlan] >= 2 && (
                <section className="card" style={{ marginTop: '2rem', marginBottom: '2rem', background: !nextBillingDate ? '#fef3c7' : 'var(--surface)' }}>
                    {/* Expiring Subscription Warning (active but no next billing = canceled pending expiry) */}
                    {!nextBillingDate && (
                        <div style={{
                            background: '#fbbf24',
                            color: '#78350f',
                            padding: '0.75rem 1rem',
                            borderRadius: 'var(--radius)',
                            marginBottom: '1rem',
                            fontWeight: 500
                        }}>
                            ⚠️ Your subscription is set to expire at the end of your current billing period.
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                            <h3 style={{ margin: 0 }}>
                                {!nextBillingDate ? 'Revert Cancellation' : 'Manage Your Subscription'}
                            </h3>
                            <p style={{ color: 'var(--text-secondary)', margin: '0.5rem 0 0' }}>
                                {!nextBillingDate
                                    ? `Your ${userPlan} plan will expire soon. Revert cancellation to keep your benefits.`
                                    : `You're on the ${userPlan} plan. Update payment method or cancel anytime.`
                                }
                            </p>
                        </div>
                        <div style={{ position: 'relative' }}>
                            {loadingManagement ? (
                                <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
                            ) : (
                                <div className="dropdown-container">
                                    {!nextBillingDate ? (
                                        /* Expiring subscription - show revert button */
                                        managementUrls?.cancelUrl && (
                                            <a
                                                href={managementUrls.cancelUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="button primary"
                                            >
                                                Revert Cancellation
                                            </a>
                                        )
                                    ) : (
                                        /* Active subscription - show manage dropdown */
                                        <>
                                            <button
                                                className="button secondary"
                                                onClick={() => setShowManageDropdown(!showManageDropdown)}
                                            >
                                                Manage ▾
                                            </button>
                                            {showManageDropdown && (
                                                <div
                                                    className="dropdown-menu"
                                                    style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        right: 0,
                                                        marginTop: '0.5rem',
                                                        background: 'var(--card-bg)',
                                                        border: '1px solid var(--border)',
                                                        borderRadius: 'var(--radius)',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                        minWidth: '180px',
                                                        zIndex: 100
                                                    }}
                                                >
                                                    {managementUrls?.updatePaymentMethodUrl && (
                                                        <a
                                                            href={managementUrls.updatePaymentMethodUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'block',
                                                                padding: '0.75rem 1rem',
                                                                color: 'var(--text-primary)',
                                                                textDecoration: 'none',
                                                                borderBottom: '1px solid var(--border)',
                                                                fontSize: '0.9rem'
                                                            }}
                                                            onClick={() => setShowManageDropdown(false)}
                                                            onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface)'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            Update Payment Method
                                                        </a>
                                                    )}
                                                    {managementUrls?.cancelUrl && (
                                                        <a
                                                            href={managementUrls.cancelUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'block',
                                                                padding: '0.75rem 1rem',
                                                                color: '#dc2626',
                                                                textDecoration: 'none',
                                                                fontSize: '0.9rem'
                                                            }}
                                                            onClick={() => setShowManageDropdown(false)}
                                                            onMouseOver={(e) => e.currentTarget.style.background = '#fef2f2'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                        >
                                                            Cancel Subscription
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};
